import { useState } from 'react'
import { useRouter } from 'next/router'
import { Row, Col, Form, Input, Button, message, Space, Select, Checkbox } from 'antd'
import Link from 'next/link'
import styles from '../../styles/login.module.css'
import { withAuth, Type } from '../../providers/auth'
import { SignupApi, apiConf } from '../../api'
import { validatePassword, passwordPolicyDescription } from '../../utils/password'
import NameInput, { validateName } from '../../components/nameInput'
import { handleApiError } from '../../utils/error'
import { searchAddress, Prefectures } from '../../utils/address'
import { normalizePhonePostalInput, normalizePostalCodeInput } from '../../utils/phonePostalNormalize'
import SignUpLayout from '../../components/layout/signup'

const Step = {
  Email: 0,
  TemporaryPassword: 1,
  NewPassword: 2,
  Login: 3,
}

function Signup({ AuthStore }) {
  const router = useRouter()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [step, setStep] = useState(Step.Email)
  const [authInfo, setAuthInfo] = useState({ email: '', password: '' })
  const [loadingAddress, setLoadingAddress] = useState(false)
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [revalidateOnChange, setRevalidateOnChange] = useState(false)

  // 住所検索
  const handleSearchAddress = async (postalCode) => {
    const normalized = normalizePostalCodeInput(postalCode ?? '')
    form.setFieldsValue({ postalCode: normalized })
    setLoadingAddress(true)
    const result = await searchAddress(normalized)
    console.warn(result)
    if (result.success) {
      form.setFieldsValue({ prefecture: result.prefecture, address: result.address, address2: result.address2 })
    }
    if (result.message) {
      message.destroy()
      message.error(result.message)
    }
    setLoadingAddress(false)
  }

  // アカウント情報を登録して仮パスワードを送信する
  const handleSubmit = async (values) => {
    try {
      setLoading(true)
      await AuthStore.logout()
      const { needsToChangePassword, cognitoUserSub } = await SignupApi(await apiConf(AuthStore)).signupOrganizationPost({
        email: values.email,
        organizationName: values.organizationName,
        ownerFamilyName: values.ownerName.familyName,
        ownerGivenName: values.ownerName.givenName,
        phoneNumber: values.phoneNumber,
        postalCode: values.postalCode,
        prefecture: values.prefecture,
        address: values.address,
        address2: values.address2,
        address3: values.address3,
      })
      setAuthInfo({ email: values.email, cognitoUserSub: cognitoUserSub })
      if (needsToChangePassword) {
        setStep(Step.TemporaryPassword)
      } else {
        setStep(Step.Login)
      }
    } catch (error) {
      handleApiError(error)
    } finally {
      setLoading(false)
    }
  }

  const handleLoginWithTemporaryPassword = async (values) => {
    try {
      setLoading(true)
      const { temporaryPassword } = values
      await AuthStore.login(authInfo.email, temporaryPassword)
      setStep(Step.NewPassword)
    } catch (error) {
      message.destroy()
      message.error(error.message)
      console.warn(error)
    } finally {
      setLoading(false)
    }
  }

  const handleLoginWithPassword = async (values) => {
    try {
      setLoading(true)
      const { password } = values
      await AuthStore.login(authInfo.email, password, true)
      router.push('/signup/license')
    } catch (error) {
      message.destroy()
      message.error(error.message)
      console.warn(error)
    } finally {
      setLoading(false)
    }
  }

  // パスワードを登録
  const handleSetNewPassword = async (values) => {
    try {
      setLoading(true)
      const { newPassword } = values
      await AuthStore.completeNewPassword(newPassword, true)
      message.success('パスワードを登録しました')
      router.push('/signup/license')
    } catch (error) {
      if (error.message === 'Invalid session provided for the user.') {
        message.error('不正なセッションです。ログイン画面からログインを行なってください')
        router.push('/login')
        return
      }
      message.destroy()
      message.error(error.message)
      console.warn(error)
    } finally {
      setLoading(false)
    }
  }

  // 認証コードを再送する
  const handleResendVerificationEmail = async () => {
    try {
      setLoading(true)
      await SignupApi(await apiConf(AuthStore)).signupResendPasswordPost({ cognitoUserSub: authInfo.cognitoUserSub })
      message.destroy()
      message.success('仮パスワードを送信しました。メールをご確認ください。')
    } catch (error) {
      message.destroy()
      // 登録済みの既存ユーザーの場合はエラーになるが、第三者によるアカウント確認を避けるため成功メッセージを表示
      message.success('仮パスワードを送信しました。メールをご確認ください。')
    } finally {
      setLoading(false)
    }
  }

  const renderEmailForm = () => {
    const validateTrigger = revalidateOnChange ? ['onSubmit', 'onChange'] : 'onSubmit'
    return (
      <div className={styles.content}>
        <div style={{ textAlign: 'center', marginBottom: 32, padding: '0 32px' }}>
          <img src="/img/logo.png" alt="みんなの契約書" style={{ maxWidth: 400, width: '100%', marginTop: 64 }} />
        </div>

        <h1 className={styles.pageTitle}>アカウント新規作成</h1>
        <Form form={form} layout="vertical" onFinish={handleSubmit} onFinishFailed={() => setRevalidateOnChange(true)} style={{ marginBottom: 32 }}>
          <Row type="flex" justify="center" align="middle">
            <Col xs={24} sm={20} md={16} lg={14} xl={12}>
              <div className={styles.formContainer}>
                <Form.Item
                  name="organizationName"
                  validateTrigger={validateTrigger}
                  rules={[
                    { required: true, message: '会社名を入力してください' },
                    { max: 50, message: '会社名は50文字以内で入力してください' },
                  ]}
                  label="会社名">
                  <Input size="large" placeholder="株式会社 みんなの契約書" />
                </Form.Item>
                <Form.Item
                  name="postalCode"
                  validateTrigger={validateTrigger}
                  getValueFromEvent={(e) => normalizePostalCodeInput(e?.target?.value ?? '')}
                  normalize={normalizePostalCodeInput}
                  rules={[
                    { required: true, message: '郵便番号を入力してください' },
                    {
                      validator: (_, value) => {
                        const v = (value ?? '').trim()
                        if (!v) return Promise.resolve()
                        if (!/^\d{3}-\d{4}$/.test(v)) {
                          return Promise.reject(new Error('郵便番号は「123-4567」の形式で入力してください'))
                        }
                        return Promise.resolve()
                      },
                    },
                  ]}
                  label="郵便番号">
                  <Input.Search
                    size="large"
                    placeholder="例: 123-4567"
                    enterButton={
                      <Button type="primary" size="large" loading={loadingAddress}>
                        住所検索
                      </Button>
                    }
                    onSearch={handleSearchAddress}
                  />
                </Form.Item>
                <Form.Item
                  name="prefecture"
                  validateTrigger={validateTrigger}
                  rules={[{ required: true, message: '都道府県を入力してください' }]}
                  label="都道府県">
                  <Select size="large" placeholder="都道府県" options={Prefectures.map((prefecture) => ({ label: prefecture, value: prefecture }))} />
                </Form.Item>
                <Form.Item
                  name="address"
                  validateTrigger={validateTrigger}
                  rules={[
                    { required: true, message: '住所1を入力してください' },
                    { max: 50, message: '住所1は50文字以内で入力してください' },
                  ]}
                  label="住所1">
                  <Input size="large" placeholder="市区町村" />
                </Form.Item>
                <Form.Item
                  name="address2"
                  validateTrigger={validateTrigger}
                  rules={[
                    { required: true, message: '住所2を入力してください' },
                    { max: 50, message: '住所2は50文字以内で入力してください' },
                  ]}
                  label="住所2">
                  <Input size="large" placeholder="町名・番地" />
                </Form.Item>
                <Form.Item
                  name="address3"
                  validateTrigger={validateTrigger}
                  rules={[{ max: 50, message: '住所3は50文字以内で入力してください' }]}
                  label="住所3">
                  <Input size="large" placeholder="建物名など" />
                </Form.Item>
                <Form.Item
                  name="phoneNumber"
                  validateTrigger={validateTrigger}
                  getValueFromEvent={(e) => normalizePhonePostalInput(e?.target?.value ?? '')}
                  normalize={normalizePhonePostalInput}
                  rules={[
                    { required: true, message: '電話番号を入力してください' },
                    { max: 13, message: '電話番号は13文字以内で入力してください' },
                    {
                      validator: (_, value) => {
                        const v = (value ?? '').trim()
                        if (!v) return Promise.resolve()
                        if (!/^[0-9-]+$/.test(v)) {
                          return Promise.reject(new Error('電話番号は数字とハイフン（-）のみで入力してください'))
                        }
                        if (!v.includes('-')) {
                          return Promise.reject(new Error('電話番号はハイフン（-）で区切って入力してください'))
                        }
                        if (!/^\d+-\d+-\d+$/.test(v)) {
                          return Promise.reject(new Error('電話番号の形式が正しくありません'))
                        }
                        return Promise.resolve()
                      },
                    },
                  ]}
                  label="電話番号">
                  <Input size="large" type="tel" placeholder="例: 03-1234-5678" />
                </Form.Item>
                <Form.Item
                  name="ownerName"
                  label="担当者氏名"
                  validateTrigger={validateTrigger}
                  initialValue={{ familyName: '', givenName: '' }}
                  required
                  rules={[
                    {
                      validator: validateName,
                    },
                  ]}>
                  <NameInput />
                </Form.Item>
                <Form.Item
                  name="email"
                  validateTrigger={validateTrigger}
                  rules={[
                    { required: true, message: 'メールアドレスを入力してください' },
                    { max: 255, message: 'メールアドレスは255文字以内で入力してください' },
                  ]}
                  label="メールアドレス">
                  <Input type="email" size="large" placeholder="メールアドレス" />
                </Form.Item>
                <div style={{ textAlign: 'center', marginTop: 32, marginBottom: 32 }}>
                  <Checkbox checked={isConfirmed} onChange={(event) => setIsConfirmed(event.target.checked)}>
                    <a href="https://minnano-keiyakusho.jp/terms" target="_blank" rel="noopener noreferrer" style={{ marginLeft: 4, marginRight: 4 }}>
                      利用規約
                    </a>
                    及び
                    <a href="https://www.orso.jp/privacypolicy.html" target="_blank" rel="noopener noreferrer" style={{ marginLeft: 4, marginRight: 4 }}>
                      プライバシーポリシー
                    </a>
                    に同意する
                  </Checkbox>
                </div>
                <Button type="primary" htmlType="submit" size="large" block loading={loading} className={styles.loginButton} disabled={!isConfirmed}>
                  次へ
                </Button>
              </div>
              <div className={styles.link}>
                <Link href="/login">アカウントをすでにお持ちの方</Link>
              </div>
            </Col>
          </Row>
        </Form>
      </div>
    )
  }

  // 仮パスワードでログインする
  const renderTemporaryPasswordLoginForm = () => {
    return (
      <div className={styles.content}>
        <Form form={form} layout="vertical" requiredMark={false} onFinish={handleLoginWithTemporaryPassword} className={styles.loginForm}>
          <Row type="flex" justify="center" align="middle">
            <Col xs={24} sm={20} md={16} lg={14} xl={12}>
              <h1 className={styles.pageTitle}>仮パスワードの入力</h1>
              <div className={styles.pageDescription}>
                ご入力いただいたメールアドレス宛に仮パスワードを送信しました。 <br />
                メールの本文に記載されている英数字列を入力してください。
              </div>
              <div className={styles.formContainer}>
                <Form.Item name="email" rules={[{ required: true, message: 'メールアドレスを入力してください' }]} label="メールアドレス">
                  <Input size="large" placeholder="メールアドレス" readOnly value={authInfo.email} />
                </Form.Item>
                <Form.Item name="temporaryPassword" rules={[{ required: true, message: '仮パスワードを入力してください' }]} label="仮パスワード">
                  <Input type="password" size="large" placeholder="仮パスワード" />
                </Form.Item>
                <Button type="primary" htmlType="submit" size="large" block loading={loading} className={styles.loginButton}>
                  次へ
                </Button>
              </div>
              <div className={styles.link}>
                <a onClick={handleResendVerificationEmail}>仮パスワードを再送する</a>
              </div>
            </Col>
          </Row>
        </Form>
      </div>
    )
  }

  // ログインする
  const renderLoginForm = () => {
    return (
      <div className={styles.content}>
        <h1 className={styles.title}>ログイン</h1>
        <Form form={form} layout="vertical" requiredMark={false} onFinish={handleLoginWithPassword} className={styles.loginForm}>
          <Row type="flex" justify="center" align="middle">
            <Col xs={24} sm={20} md={16} lg={14} xl={12}>
              <div className={styles.formContainer}>
                <Form.Item name="email" rules={[{ required: true, message: 'メールアドレスを入力してください' }]} label="メールアドレス">
                  <Input size="large" placeholder="メールアドレス" readOnly value={authInfo.email} />
                </Form.Item>
                <Form.Item name="password" rules={[{ required: true, message: 'パスワードを入力してください' }]} label="パスワード">
                  <Input type="password" size="large" placeholder="パスワード" />
                </Form.Item>
                <Button type="primary" htmlType="submit" size="large" block loading={loading} className={styles.loginButton}>
                  ログイン
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </div>
    )
  }

  // 新しいパスワードを登録する
  const renderPasswordForm = () => {
    return (
      <div className={styles.content}>
        <Form form={form} layout="vertical" requiredMark={false} onFinish={handleSetNewPassword} className={styles.loginForm}>
          <Row type="flex" justify="center" align="middle">
            <Col xs={24} sm={20} md={16} lg={14} xl={12}>
              <h1 className={styles.pageTitle}>パスワードの設定</h1>
              <div className={styles.pageDescription}>ログイン時に使用するパスワードを設定してください。</div>
              <div className={styles.formContainer}>
                <Form.Item
                  name="newPassword"
                  label="パスワード"
                  extra={<span style={{ fontSize: 14 }}>{passwordPolicyDescription()}</span>}
                  rules={[
                    { required: true, message: 'パスワードを入力してください' },
                    { max: 99, message: 'パスワードが長すぎます' },
                    { validator: validatePassword(), validateTrigger: 'onSubmit' },
                  ]}>
                  <Input.Password type="password" size="large" placeholder="パスワード" />
                </Form.Item>
                <Button type="primary" htmlType="submit" size="large" block loading={loading} className={styles.loginButton}>
                  次へ
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </div>
    )
  }

  const handleBack = () => {
    setStep(Step.Email)
  }

  const renderForm = () => {
    switch (step) {
      case Step.Email:
        return <div className={styles.container}>{renderEmailForm()}</div>
      case Step.TemporaryPassword:
        return (
          <SignUpLayout nomenu title="アカウント新規作成" back={handleBack}>
            {renderTemporaryPasswordLoginForm()}
          </SignUpLayout>
        )
      case Step.NewPassword:
        return (
          <SignUpLayout nomenu title="アカウント新規作成" back={handleBack}>
            {renderPasswordForm()}
          </SignUpLayout>
        )
      case Step.Login:
        return (
          <SignUpLayout nomenu title="アカウント新規作成" back={handleBack}>
            {renderLoginForm()}
          </SignUpLayout>
        )
    }
  }

  return <>{renderForm()}</>
}

export default withAuth(Signup, Type.NotProtected)
